import { apiPost } from '@/api/axios-client'
import { getErrorMessage, isApiError } from '@/api/errors'

/**
 * Getting bytes into storage, from a browser that holds no AWS credential.
 *
 * There are two paths and the caller picks neither. The fast one asks the API for a URL good
 * for one file for five minutes and PUTs straight to S3, which has no practical size ceiling.
 * That PUT is cross-origin, so it needs a CORS rule on the bucket — there isn't one, and
 * adding it needs a permission nobody on this account holds, so the browser refuses to send
 * it every time. A rejected upload falls back to sending the bytes through our own API, which
 * the browser is always allowed to talk to.
 *
 * In practice the fallback IS the path, which is why the advertised limit is 10MB rather than
 * 25. The fast path resumes on its own the day a CORS rule is applied.
 *
 * Lives in `lib/` rather than inside the attachments field because it is not about
 * attachments: a profile photo takes the identical route, and duplicating it would mean the
 * two learn about a CORS rule separately.
 */

/**
 * 10MB, which is what actually works — not the 25MB the signed path could carry.
 *
 * Every upload goes through the API, and a Lambda is handed at most 6MB of request payload
 * with API Gateway base64-encoding binary on the way in. A single request therefore carries
 * four; bigger files travel as three-megabyte pieces the API joins.
 *
 * Advertising 25 and refusing at 4 is worse than advertising 4: somebody picks a file,
 * watches it fail, and concludes uploads are broken. When a CORS rule is applied this goes
 * back up — one line here, and the hints that quote it follow.
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
/** One request carries at most this much; a bigger file goes up in pieces and is joined by the API. */
const SINGLE_REQUEST_BYTES = 4 * 1024 * 1024
const CHUNK_BYTES = 3 * 1024 * 1024
export const MAX_UPLOAD_MB = MAX_UPLOAD_BYTES / 1024 / 1024

/**
 * Whether the signed path is known to be unusable in this session.
 *
 * Module-level rather than per-component: it is a fact about the bucket, not about one form,
 * and re-learning it costs a doomed request per file. Reset on reload, so applying a CORS
 * rule takes effect for everyone at their next visit without a deploy.
 */
let directOnly = false

/** The fast path: the browser sends the bytes straight to S3. Needs a CORS rule on the bucket. */
async function viaSignedUrl(file: Blob, name: string, contentType: string): Promise<string> {
  const signed = await apiPost<{ key: string; url: string }>('/uploads/sign', {
    name,
    contentType,
    size: file.size,
  })

  /*
   * A bare fetch, deliberately — not the API client.
   *
   * The signature covers the headers it was signed with. The client's Authorization header
   * would arrive as an unsigned extra and S3 would reject the whole PUT, which reads as
   * "uploads are broken" rather than "that header does not belong here".
   */
  const res = await fetch(signed.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  })
  if (!res.ok) throw new Error(`Storage refused the upload (${res.status}).`)
  return requireKey(signed?.key)
}

/** The fallback: the bytes go through our own API, which the browser may always talk to. */
async function viaApi(file: Blob, name: string, contentType: string): Promise<string> {
  // Video has no ceiling here: the API joins its pieces with S3 multipart (the backend's MAX_VIDEO_BYTES is the backstop).
  if (file.size > MAX_UPLOAD_BYTES && !contentType.startsWith('video/')) {
    throw new Error(`Larger than the ${MAX_UPLOAD_MB}MB limit.`)
  }
  if (file.size > SINGLE_REQUEST_BYTES) return viaChunks(file, name, contentType)
  const q = `?name=${encodeURIComponent(name)}&contentType=${encodeURIComponent(contentType)}`
  const res = await apiPost<{ key: string; name: string }>(`/uploads/direct${q}`, file, {
    headers: { 'Content-Type': 'application/octet-stream' },
  })
  return requireKey(res?.key)
}

/** Larger files: three-megabyte pieces, each under the Lambda payload ceiling, then one join. */
async function viaChunks(file: Blob, name: string, contentType: string): Promise<string> {
  const uploadId = crypto.randomUUID()
  const total = Math.ceil(file.size / CHUNK_BYTES)
  for (let index = 0; index < total; index += 1) {
    const piece = file.slice(index * CHUNK_BYTES, (index + 1) * CHUNK_BYTES)
    await apiPost(`/uploads/chunk?uploadId=${uploadId}&index=${index}`, piece, {
      headers: { 'Content-Type': 'application/octet-stream' },
    })
  }
  const res = await apiPost<{ key: string; name: string }>('/uploads/assemble', { uploadId, total, name, contentType })
  return requireKey(res?.key)
}

/**
 * The storage key, or a refusal.
 *
 * `apiPost` unwraps the envelope and the generic argument is an assertion, not a check — so
 * a response that does not carry `key` under that name resolves `undefined` here and the
 * declared `Promise<string>` is simply a lie. That `undefined` then travels: it is sent as
 * the key, serialises out of the JSON body entirely, and the write becomes a no-op that
 * nobody reports. Failing at the boundary turns a silent wrong answer into a sentence.
 */
function requireKey(key: unknown): string {
  if (typeof key !== 'string' || key.length === 0) {
    throw new Error('Storage accepted the file but did not say where it put it.')
  }
  return key
}

/**
 * Upload one blob and return the storage key.
 *
 * Takes a Blob rather than a File because not everything uploaded came off a disk — a
 * profile photo is cropped on a canvas first, and what reaches here is bytes with a name we
 * chose.
 */
export async function uploadBlob(
  blob: Blob,
  name: string,
  contentType: string = blob.type,
): Promise<string> {
  try {
    return directOnly
      ? await viaApi(blob, name, contentType)
      : await viaSignedUrl(blob, name, contentType)
  } catch (err) {
    /*
     * A cross-origin fetch rejected by CORS throws a TypeError and says nothing else. On a
     * bucket with no CORS rule that is EVERY signed upload, so rather than surfacing the same
     * failure per file, fall back to sending the bytes through our own API — which the
     * browser is already allowed to talk to — and remember it for the rest of the session.
     */
    if (err instanceof TypeError && !directOnly && navigator.onLine) {
      directOnly = true
      return viaApi(blob, name, contentType)
    }
    throw err
  }
}

/**
 * Say what actually went wrong, rather than repeating the browser's word for it.
 *
 * A `fetch` blocked by CORS rejects with the bare message "Failed to fetch" — the browser
 * refuses to say more, because telling a page why a cross-origin request was rejected is
 * itself an information leak. Surfaced verbatim, that reads as "the app is broken" when the
 * real cause is a bucket missing a CORS rule: a one-line configuration fix that nobody can
 * guess from those three words.
 *
 * Axios is no better: a request that never reached a server rejects as "Network Error", which
 * names the layer rather than the problem and reads to somebody uploading a photo as though
 * the app were broken.
 */
export function describeUploadFailure(err: unknown): string {
  const unreachable = err instanceof TypeError || (isApiError(err) && err.code === 'NETWORK')
  if (unreachable) {
    // Reached only when the fallback through the API also failed, or we are offline.
    return navigator.onLine
      ? 'Could not reach storage. Check your connection and try again.'
      : 'You appear to be offline.'
  }
  return getErrorMessage(err)
}
